import { createRoot } from "react-dom/client";
import { Header } from "@viveportengineering/share-component";
import "@viveportengineering/share-component/style.css";
import type {
  AuthConfig,
  Device,
  HeaderProps,
  Locale,
  LocaleConfig,
  OriginConfig,
  Profile,
} from "@viveportengineering/share-component";

const apiBaseUrl = "https://www.viverse.com";
const worldApi = "https://world-api.viverse.com/";
const worldApiVersion = "v4";

const localePathMap: Partial<Record<Locale, string>> = {
  "ja-JP": "ja",
  "zh-TW": "zh-TW",
  "nl-NL": "nl",
  "de-DE": "de",
  "fr-FR": "fr",
  "pt-BR": "pt-br",
  "ru-RU": "ru",
  "it-IT": "it",
  "pl-PL": "pl",
  "es-ES": "es",
  "hi-IN": "hi",
};

const localePathRegex = new RegExp(
  `^/(${Object.values(localePathMap).join("|")}|zh)(?=/|$)`,
  "i",
);

const localeByPath = Object.entries(localePathMap).reduce<
  Record<string, Locale>
>(
  (acc, [locale, path]) =>
    path ? { ...acc, [path.toLowerCase()]: locale as Locale } : acc,
  { zh: "zh-TW" },
);

type TokenResponse = {
  success?: boolean;
  message?: string;
  token?: string;
  accountId?: string;
};

type WorldProfileResponse = {
  display_name?: string;
  community_user_id?: string;
  htc_account_id?: string;
  active_avatar?: Profile["activeAvatar"];
};

type UserPointsResponse = {
  success: boolean;
  message?: string;
  data?: {
    points?: number;
  };
};

type ChangeLocaleDetail = {
  locale: Locale;
};

type ChangeRouteDetail = {
  origin: string;
  path: string;
};

const setCookie = (
  name: string,
  value: string,
  days: number,
  domain?: string,
) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  const domainPart = domain ? `; domain=${domain}` : "";
  document.cookie =
    `${name}=${encodeURIComponent(value)}${expires}; path=/` + domainPart;
};

const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

const cookieDomain =
  window.location.hostname === "localhost"
    ? "localhost"
    : (function getCookieDomain(url: string) {
        try {
          const { hostname } = new URL(url);
          const parts = hostname.split(".");
          if (parts.length > 2) {
            return parts.slice(parts.length - 2).join(".");
          }
          return hostname;
        } catch {
          return window.location.hostname;
        }
      })(window.location.origin);

const originConfig: OriginConfig = {
  blog: "https://news.viverse.com",
  viverse: "https://www.viverse.com",
  world: "https://worlds.viverse.com",
  market: "https://market.viverse.com",
  avatar: "https://avatar.viverse.com",
  create: "https://create.viverse.com",
  document: "https://docs.viverse.com",
  viveport: "https://www.viveport.com",
  beatday: "https://beatday.com",
  lab: "https://www.viverse.com/lab/object",
  studio: "https://studio.viverse.com",
  persona: "https://persona.viverse.com",
};

const getCallbackUrl = () => {
  const locale = window.location.pathname.match(localePathRegex)?.[1];
  const localePath = locale ? `/${locale}` : "";

  return `${window.location.origin}${localePath}`;
};

const getCurrentLocale = (): Locale => {
  const urlLocale = window.location.pathname.match(localePathRegex)?.[1];
  const locale = localeByPath[urlLocale?.toLowerCase() || ""];

  return locale || "en-US";
};

const getDevice = (): Device =>
  window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop";

const getWorldProfile = async (
  token: string,
): Promise<WorldProfileResponse> => {
  const response = await fetch(
    `${worldApi}api/hubs-cms/${worldApiVersion}/me`,
    {
      method: "GET",
      headers: {
        AuthKey: token,
      },
    },
  );

  if (!response.ok) {
    console.error(`Error fetching profile: ${response.statusText}`);
  }

  return await response.json();
};

const getUserPoints = async (token: string): Promise<UserPointsResponse> => {
  const response = await fetch(`${apiBaseUrl}/api/point-service/v1/points`, {
    method: "GET",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authkey: token,
    },
  });

  const { status, statusText } = response;
  const result = await response.json();

  if (status === 200) {
    return {
      success: true,
      data: { points: result.data?.pointBalance },
    };
  }

  console.error(`Error: ${statusText}`);
  return {
    success: false,
    message: `Error: ${statusText}`,
  };
};

const getToken = async (): Promise<TokenResponse> => {
  const response = await fetch(`${apiBaseUrl}/api/check-token`, {
    method: "GET",
    credentials: "include",
    headers: {
      "User-Agent": "TG3D",
      "Content-Type": "application/json; charset=UTF-8",
    },
  });

  if (!response.ok || response.status === 204) {
    return { success: false };
  }

  const responseText = await response.text();
  if (!responseText) {
    return { success: false };
  }

  try {
    return JSON.parse(responseText) as TokenResponse;
  } catch {
    return { success: false };
  }
};

const login = () => {
  const currentLocale = (getCookie("_htc_lang_code") || "en_US").replace(
    "_",
    "-",
  );
  const loginUrl = `${apiBaseUrl}/api/login?hl=${currentLocale}&callback_url=${getCallbackUrl()}`;
  window.location.assign(loginUrl);
};

const logout = () => {
  const logoutUrl = `${apiBaseUrl}/api/logout?redirect_url=${getCallbackUrl()}`;
  window.location.assign(logoutUrl);
};

const changeLocale = (locale: Locale) => {
  setCookie("_htc_lang_code", locale.replace("-", "_"), 365, cookieDomain);
  const localePath = localePathMap[locale] ? `/${localePathMap[locale]}` : "";
  const normalizedPathname = window.location.pathname
    .replace(localePathRegex, "")
    .replace(/^$/, "/");
  const pathWithLocale =
    normalizedPathname === "/"
      ? localePath || "/"
      : `${localePath}${normalizedPathname}`;

  window.location.assign(
    `${pathWithLocale}${window.location.search}${window.location.hash}`,
  );
};

const changeRoute = (origin = originConfig.viverse, path = "/") => {
  window.location.href = `${origin}${path}`;
};

const createProfile = async (): Promise<Profile> => {
  const tokenResponse = await getToken();
  const { token, accountId } = tokenResponse;

  if (!token || !accountId) {
    return {};
  }

  const [profileResponse, pointsResponse] = await Promise.all([
    getWorldProfile(token),
    getUserPoints(token),
  ]);

  return {
    displayName: profileResponse.display_name || "",
    userID: profileResponse.community_user_id || "",
    accountId: profileResponse.htc_account_id || accountId,
    token,
    activeAvatar: profileResponse.active_avatar,
    points: Number(pointsResponse.data?.points || 0),
  };
};

const mount = async () => {
  const headerRef = document.getElementById("header");

  if (!headerRef) return;

  const headerRoot = createRoot(headerRef);

  const render = (profile: Profile = {}) => {
    const currentLocale = getCurrentLocale();
    const device = getDevice();
    const authConfig: AuthConfig = {
      onLogin: login,
      onLogout: logout,
      changeRoute,
      profile,
    };
    const localeConfig: LocaleConfig = {
      locale: currentLocale,
    };
    const layoutConfig: HeaderProps["layoutConfig"] = {
      currentOrigin: originConfig.blog,
      desktopDrawerInteractionMode: "hover",
      tab: "BLOG",
      community: {
        isShowBlueDot: false,
        showBlueDot: () => {},
        showIframe: () => {},
      },
    };

    headerRoot.render(
      <Header
        authConfig={authConfig}
        localeConfig={localeConfig}
        layoutConfig={layoutConfig}
        origin={originConfig}
        device={device}
      />,
    );
  };

  render();

  try {
    render(await createProfile());
  } catch (error) {
    console.error(error);
  }

  const handleLocaleEvent = (event: Event) => {
    const { detail } = event as CustomEvent<ChangeLocaleDetail>;
    changeLocale(detail.locale);
  };
  const handleRouteEvent = (event: Event) => {
    const { detail } = event as CustomEvent<ChangeRouteDetail>;
    changeRoute(detail.origin, detail.path);
  };

  document.removeEventListener("onChangeLocale", handleLocaleEvent);
  document.removeEventListener("onChangeRoute", handleRouteEvent);
  document.addEventListener("onChangeLocale", handleLocaleEvent);
  document.addEventListener("onChangeRoute", handleRouteEvent);
};

mount();
